<template>
  <div>
    <h2 class="mb-5">{{ defaultHeader }}</h2>

    <!-- Upload Modal -->
    <hl-Modal v-if="showUploadFileModal" title="Last opp fil" class="text-center">
      <div>
        <p>Velg en fil du vil laste opp, deretter velg en kategori og klikk så “Last opp”.</p>

        <!-- There are files -->
        <div v-if="files.length">
          <ul class="files-to-be-uploaded list-unstyled text-left">
            <li v-for="(file, i) in files" :key="i">
              <div class="row align-items-center">
                <div class="col">{{ file.fileName }}</div>
                <div class="col text-secondary text-right">
                  <hl-Button size="sm" variant="white" @click="removeDocumentToBeUploaded">
                    <i class="budicon-trash"></i>
                  </hl-Button>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- NO files. Show upload -->
        <div v-else class="input-group mb-3 mt-5">
          <div class="custom-file">
            <input
              type="file"
              @change="handleFilesUpload()"
              class="custom-file-input"
              id="files"
              ref="files"
              aria-describedby="inputGroupFileAddon01"
            />
            <label class="custom-file-label" for="inputGroupFile01">Velg fil</label>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12 col-md-6">
            <hl-Select
              label="Kategori"
              placeholder="Velg"
              :options="fileCategoryOptions"
              v-model="newFile.category"
            ></hl-Select>
          </div>
        </div>

        <div class="mt-5">
          <hl-Button variant="secondary" outline class="mx-2" @click="closeUploadFileModal">Avbryt</hl-Button>
          <hl-Button variant="primary" class="mx-2" @click="submitFiles" :disabled="!newFile.category || !files.length"
            >Last opp</hl-Button
          >
        </div>
      </div>
    </hl-Modal>
    <!-- / Upload Modal -->

    <!-- Notes -->
    <BlockContainer class="p-5">
      <ValidationObserver v-slot="{ invalid }">
        <h5 class="d-inline-block">Notater</h5>

        <div class="bg-light mt-5 p-4 comments">
          <div class="row">
            <div class="col-12 col-md-6">
              <h6>Notater ({{ notes.length }})</h6>

              <div v-if="loading.notes" class="row text-center">
                <div class="col-12">
                  <hl-Loader :size="'sm'" class="mt-4" />
                </div>
              </div>
              <template v-else>
                <div class="comments-container mt-4">
                  <div v-for="note in notes" :key="note.id" class="card mb-4">
                    <div class="row align-items-center">
                      <div class="col-10">
                        <h6 class="m-0">{{ note.author }}</h6>
                      </div>

                      <div class="col-2 text-right">
                        <i class="budicon-trash" @click="removeNote(note.id)"></i>
                      </div>
                    </div>

                    <p class="timestamp mt-1">
                      <hl-Date :date="note.date.created" />
                    </p>

                    <pre class="mt-2 formatted-note">{{ note.body }}</pre>
                  </div>
                </div>
              </template>
            </div>

            <div class="col-12 col-md-6">
              <h6>Legg til notat</h6>
              <ValidationProvider name="Notat" :rules="'required'" v-slot="{ errors }">
                <hl-Textarea
                  class="mt-3"
                  v-model="newComment"
                  placeholder="Skriv notat relatert til leieforholdet. Dette er ikke synlig for leietaker."
                  :rows="5"
                  :error="errors[0]"
                ></hl-Textarea>
              </ValidationProvider>
              <div class="text-right">
                <hl-Button variant="primary" @click="saveNote" :loading="loading.post" :disabled="invalid"
                  >Lagre</hl-Button
                >
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </BlockContainer>
    <!-- / Notes -->

    <!-- Documents -->
    <BlockContainer class="p-5 mt-4">
      <h5>Dokumenter</h5>

      <div v-if="loading.documents" class="row text-center">
        <div class="col-12">
          <hl-Loader :size="'sm'" class="mt-4" />
        </div>
      </div>
      <template v-else>
        <List v-if="documents.length" class="mt-5">
          <ListHeader>
            <div class="col date">Dato</div>
            <div class="col">Filnavn</div>
            <div class="col category">Kategori</div>
            <!-- <div class="col tenantHasAccess text-center">Tenant has access</div> -->
            <div class="col actions text-right">&nbsp;</div>
          </ListHeader>

          <div>
            <ListItem v-for="document in documents" :key="document.id">
              <div class="col date">
                <hl-Date :date="document.created"></hl-Date>
              </div>
              <div class="col">
                <hl-Logo v-if="document.isHusleieDocument" :text="false" :width="19" class="d-inline mr-2"></hl-Logo>
                <span>{{ document.fileName }}</span>
              </div>
              <div class="col category">{{ documentCategoryMap[document.category] }}</div>
              <!-- <div class="col tenantHasAccess text-center">
                  <i v-if="!document.hasTenantAccess" class="budicon-block-sign text-danger"></i>
              </div>-->
              <div class="col actions text-right">
                <ActionsMenu class="list-item-card-actions" :menuItems="documentsActionsMenuItems(document)" />
              </div>
            </ListItem>
          </div>
        </List>
        <div v-else class="mt-5 text-center">
          <img src="@/assets/empty-interest-management.svg" width="220" />

          <h6 class="mt-5">Det er ikke lastet opp noen dokumenter enda</h6>
        </div>
        <div class="mt-4 pt-3 text-center">
          <button class="btn btn-primary" @click="openUploadFileModal">Last opp fil</button>
        </div>
      </template>
    </BlockContainer>
    <!-- / Documents -->
  </div>
</template>

<script>
import axios from 'axios';
import ActionsMenu from '@/components/UI/ActionsMenu';
import BlockContainer from '@/components/BlockContainer.vue';
import List from '@/components/UI/list/List.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import { mapGetters, mapActions } from 'vuex';
import { TenancyApi } from '@modules/husleieapi2';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Textarea from '@modules/husleie-framework/src/components/forms/Textarea.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Logo from '@modules/husleie-framework/src/components/logo/Logo.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';

export default {
  components: {
    ActionsMenu,
    BlockContainer,
    List,
    ListHeader,
    ListItem,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Select': Select,
    'hl-Textarea': Textarea,
    'hl-Date': Date,
    'hl-Logo': Logo,
    'hl-Loader': Loader,
  },

  props: {
    rentalObjectId: [String, Number],
    landlordId: [String, Number],
    tenancyId: [String, Number],
    defaultHeader: String,
  },

  data() {
    return {
      newComment: '',
      showUploadFileModal: false,
      newFile: {
        file: '',
        category: '',
      },
      documents: [],
      notes: [],
      documentCategoryMap: {
        contract: 'leiekontrakt',
        depgar: 'depositumsgaranti',
        takeover_protocol: 'overtakelsesprotokoll',
        inspection_report: 'overtakelsesprotokoll',
        inspection_report_moveout: 'utflyttingsprotokoll',
        other: 'andre',
      },
      loading: {
        post: false,
        notes: false,
        documents: false,
      },
      // uploaded files
      files: [],
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancyNotes']),

    fileCategoryOptions() {
      return [
        {
          name: 'Leiekontrakt',
          value: 'contract',
        },
        {
          name: 'Depositumsgaranti',
          value: 'depgar',
        },
        {
          name: 'Overtakelsesprotokoll',
          value: 'takeover_protocol',
        },
        {
          name: 'Overtakelsesprotokoll',
          value: 'inspection_report',
        },
        {
          name: 'Utflyttingsprotokoll',
          value: 'inspection_report_moveout',
        },
        {
          name: 'Andre',
          value: 'other',
        },
      ];
    },
  },

  methods: {
    ...mapActions('tenancy', [
      'postTenancyNote',
      'fetchTenancyNotes',
      'deleteTenancyNote',
      'addTenancyDocument',
      'deleteTenancyDocument',
    ]),

    async fetchNotes() {
      this.loading.notes = true;
      try {
        const [landlordId, tenancyId] = [this.landlordId, this.tenancyId];
        this.notes = await this.fetchTenancyNotes({
          landlordId,
          tenancyId,
        });
      } catch (err) {
        console.error(err);
        this.$toast('Kunne ikke hente notater', 'error');
      } finally {
        this.loading.notes = false;
      }
    },

    async fetchTenancyDocuments({ landlordId, tenancyId }) {
      if (landlordId == null || tenancyId == null) return;
      try {
        const api = new TenancyApi();
        const res = await api.getLandlordTenancyDocuments(landlordId, tenancyId);
        return res;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente dokumenter', 'error');
      }
    },

    async fetchDocuments() {
      this.loading.documents = true;
      try {
        const [landlordId, tenancyId] = [this.landlordId, this.tenancyId];
        this.documents = await this.fetchTenancyDocuments({
          landlordId,
          tenancyId,
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.loading.documents = false;
      }
    },

    async saveNote() {
      this.loading.post = true;
      try {
        await this.postTenancyNote({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: this.newComment,
        });
        this.newComment = '';
        this.fetchNotes();
      } catch (error) {
        alert(error);
        console.error(error);
      } finally {
        this.loading.post = false;
      }
    },

    async removeNote(noteId) {
      this.loading.post = true;
      try {
        await this.deleteTenancyNote({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          noteId,
        });
        this.fetchNotes();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette notat.', 'error');
      } finally {
        this.loading.post = false;
      }
    },

    documentsActionsMenuItems(document) {
      return [
        {
          name: 'Last ned dokument',
          link: {
            href: document.mediaLinks[0].url,
            rel: 'noopener',
            target: '_blank',
          },
        },
        document.deletable
          ? {
              name: 'Slett dokument',
              action: () => this.removeDocument(document.id),
            }
          : undefined,
      ].filter(item => item);
    },

    handleFilesUpload() {
      const uploadedFiles = this.$refs.files.files;
      const startingIdx = this.files.length;

      for (let i = 0; i < uploadedFiles.length; i++) {
        this.files.push({
          order: startingIdx + i,
          file: uploadedFiles[i],
          fileName: uploadedFiles[i].name,
          uploaded: false,
        });
      }
    },

    submitFiles() {
      const vue = this;
      const formData = new FormData();

      for (let i = 0; i < this.files.length; i++) {
        const file = this.files[i].file;
        formData.append('files[' + i + ']', file);
      }

      axios
        .post('/upload/', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(function (response) {
          for (const file of response.data.uploaded_files) {
            vue.postDocuments(file.token);
          }
        })
        .catch(function (error) {
          console.log('FAILURE!!', error);
        });
    },

    async postDocuments(token) {
      try {
        await this.addTenancyDocument({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: {
            category: this.newFile.category,
            token,
          },
        });
        this.fetchDocuments();
        this.closeUploadFileModal();
        this.files = [];
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre dokument.', 'error');
      }
    },

    async removeDocument(docId) {
      if (docId == null) return;
      try {
        await this.deleteTenancyDocument({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          documentId: docId,
        });
        this.fetchDocuments();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette dokument.', 'error');
      }
    },

    openUploadFileModal() {
      this.showUploadFileModal = true;
    },

    closeUploadFileModal() {
      this.showUploadFileModal = false;
    },

    removeDocumentToBeUploaded() {
      this.files = [];
    },
  },

  mounted() {
    this.fetchNotes();
    this.fetchDocuments();
  },
};
</script>

<style scoped lang="scss">
.files-to-be-uploaded {
  margin-top: 1.5rem;

  li {
    font-weight: bold;
    padding: 1rem;
    background: $gray-100;
    border: 1px solid $gray-200;
    border-radius: $border-radius-medium;
  }
}

.comments {
  border: 1px solid $gray-200;
  border-radius: $border-radius-medium;
  box-shadow: $box-shadow-1;
}

.comments-container {
  max-height: 420px;
  overflow-y: scroll;
}

.card {
  border: 1px solid $gray-300;
  padding: 2rem;
  background: white;
  box-shadow: $box-shadow-1;
  width: 100%;

  .formatted-note {
    white-space: pre-wrap;
    font-family: inherit;
  }
}

.date {
  flex: 0 0 120px;
}
.tenantHasAccess {
  flex: 0 0 80px;
}

.category {
  flex: 0 0 140px;
}

.actions {
  flex: 0 0 80px;
}
</style>
